import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  Package, 
  ShoppingBag, 
  Plus, 
  Edit2, 
  Trash2, 
  X,
  Save,
  ChevronDown,
  ChevronUp,
  Settings,
  Upload,
  Loader2,
  Tag,
  Percent,
  DollarSign
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [couponDialogOpen, setCouponDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
    sizes: [],
    colors: [],
    images: [''],
    stock: '',
    featured: false
  });

  const [couponForm, setCouponForm] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: '',
    min_order_value: '',
    max_uses: '',
    expires_at: '',
    is_active: true
  });

  const categories = [
    { id: 'bras', name: 'Sutiãs' },
    { id: 'panties', name: 'Calcinhas' },
    { id: 'sets', name: 'Conjuntos' },
    { id: 'bodysuits', name: 'Bodies' },
    { id: 'sleepwear', name: 'Pijamas' },
    { id: 'robes', name: 'Robes' },
    { id: 'bikinis', name: 'Biquínis' }
  ];

  const sizes = ['PP', 'P', 'M', 'G', 'GG'];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [productsRes, ordersRes, settingsRes, couponsRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/admin/orders`),
        axios.get(`${API}/settings`),
        axios.get(`${API}/admin/coupons`).catch(() => ({ data: [] }))
      ]);
      setProducts(productsRes.data);
      setOrders(ordersRes.data);
      setSettings(settingsRes.data);
      setCoupons(couponsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setProductForm({
      name: '',
      description: '',
      price: '',
      category: '',
      sizes: [],
      colors: [],
      images: [''],
      stock: '',
      featured: false
    });
    setEditingProduct(null);
  };

  const openEditDialog = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      category: product.category,
      sizes: product.sizes,
      colors: product.colors,
      images: product.images.length > 0 ? product.images : [''],
      stock: product.stock.toString(),
      featured: product.featured
    });
    setProductDialogOpen(true);
  };

  const handleSaveProduct = async () => {
    try {
      const productData = {
        name: productForm.name,
        description: productForm.description,
        price: parseFloat(productForm.price),
        category: productForm.category,
        sizes: productForm.sizes,
        colors: productForm.colors.filter(c => c.trim()),
        images: productForm.images.filter(i => i.trim()),
        stock: parseInt(productForm.stock),
        featured: productForm.featured
      };

      if (editingProduct) {
        await axios.put(`${API}/admin/products/${editingProduct.id}`, productData);
        toast.success('Produto atualizado!');
      } else {
        await axios.post(`${API}/admin/products`, productData);
        toast.success('Produto criado!');
      }

      setProductDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Erro ao salvar produto');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!confirm('Tem certeza que deseja excluir este produto?')) return;
    
    try {
      await axios.delete(`${API}/admin/products/${productId}`);
      toast.success('Produto excluído!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir produto');
    }
  };

  const handleToggleFeatured = async (product) => {
    try {
      await axios.put(`${API}/admin/products/${product.id}`, {
        featured: !product.featured
      });
      toast.success(product.featured ? 'Removido dos destaques' : 'Adicionado aos destaques');
      fetchData();
    } catch (error) {
      toast.error('Erro ao atualizar destaque');
    }
  };

  const handleUpdateOrderStatus = async (orderId, field, value) => {
    try {
      const data = {};
      data[field] = value;
      await axios.put(`${API}/admin/orders/${orderId}/status`, data);
      toast.success('Status atualizado!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const handleUpdateTracking = async (orderId, trackingCode, trackingUrl) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status`, {
        tracking_code: trackingCode,
        tracking_url: trackingUrl
      });
      toast.success('Código de rastreio atualizado!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao atualizar rastreio');
    }
  };

  // Coupon functions
  const handleCreateCoupon = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/coupons`, {
        code: couponForm.code,
        discount_type: couponForm.discount_type,
        discount_value: parseFloat(couponForm.discount_value),
        min_order_value: couponForm.min_order_value ? parseFloat(couponForm.min_order_value) : 0,
        max_uses: couponForm.max_uses ? parseInt(couponForm.max_uses) : null,
        expires_at: couponForm.expires_at || null,
        is_active: couponForm.is_active
      });
      toast.success('Cupom criado!');
      setCouponDialogOpen(false);
      setCouponForm({
        code: '',
        discount_type: 'percentage',
        discount_value: '',
        min_order_value: '',
        max_uses: '',
        expires_at: '',
        is_active: true
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar cupom');
    }
  };

  const handleDeleteCoupon = async (couponId) => {
    if (!confirm('Tem certeza que deseja excluir este cupom?')) return;
    try {
      await axios.delete(`${API}/admin/coupons/${couponId}`);
      toast.success('Cupom excluído!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir cupom');
    }
  };

  const toggleSize = (size) => {
    setProductForm(prev => ({
      ...prev,
      sizes: prev.sizes.includes(size)
        ? prev.sizes.filter(s => s !== size)
        : [...prev.sizes, size]
    }));
  };

  const addImageField = () => {
    setProductForm(prev => ({
      ...prev,
      images: [...prev.images, '']
    }));
  };

  const updateImage = (index, value) => {
    setProductForm(prev => ({
      ...prev,
      images: prev.images.map((img, i) => i === index ? value : img)
    }));
  };

  const removeImage = (index) => {
    setProductForm(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleImageUpload = async (e, index) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Tipo de arquivo inválido. Use JPG, PNG, GIF ou WebP');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Máximo: 5MB');
      return;
    }

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/admin/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Get the full URL
      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      updateImage(index, imageUrl);
      toast.success('Imagem enviada com sucesso!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Erro ao enviar imagem');
    } finally {
      setUploadingImage(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'status-pending',
      processing: 'status-processing',
      shipped: 'status-shipped',
      delivered: 'status-delivered',
      cancelled: 'status-cancelled'
    };
    return colors[status] || 'status-pending';
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/admin/settings`, settings);
      toast.success('Configurações salvas com sucesso!');
    } catch (error) {
      toast.error('Erro ao salvar configurações');
    } finally {
      setSavingSettings(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24" data-testid="admin-dashboard">
      <div className="container-luxury py-12">
        <div className="flex justify-between items-center mb-12">
          <h1 className="font-serif text-4xl">Painel Admin</h1>
          
          {/* Stats */}
          <div className="flex gap-6">
            <div className="text-right">
              <p className="text-2xl font-bold text-primary">{products.length}</p>
              <p className="text-sm text-muted-foreground">Produtos</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-primary">{orders.length}</p>
              <p className="text-sm text-muted-foreground">Pedidos</p>
            </div>
          </div>
        </div>

        <Tabs defaultValue="products" className="space-y-8">
          <TabsList className="bg-card border border-white/10 p-1 flex flex-wrap w-full">
            <TabsTrigger 
              value="products" 
              className="flex-1 min-w-[100px] data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs sm:text-sm"
              data-testid="admin-products-tab"
            >
              <ShoppingBag className="w-4 h-4 mr-1 sm:mr-2" />
              Produtos
            </TabsTrigger>
            <TabsTrigger 
              value="orders"
              className="flex-1 min-w-[100px] data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs sm:text-sm"
              data-testid="admin-orders-tab"
            >
              <Package className="w-4 h-4 mr-1 sm:mr-2" />
              Pedidos
            </TabsTrigger>
            <TabsTrigger 
              value="settings"
              className="flex-1 min-w-[100px] data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs sm:text-sm"
              data-testid="admin-settings-tab"
            >
              <Settings className="w-4 h-4 mr-1 sm:mr-2" />
              Config
            </TabsTrigger>
            <TabsTrigger 
              value="coupons"
              className="flex-1 min-w-[100px] data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs sm:text-sm"
              data-testid="admin-coupons-tab"
            >
              <Tag className="w-4 h-4 mr-1 sm:mr-2" />
              Cupons
            </TabsTrigger>
          </TabsList>

          {/* Products Tab */}
          <TabsContent value="products" className="space-y-6">
            <div className="flex justify-end">
              <Dialog open={productDialogOpen} onOpenChange={(open) => {
                setProductDialogOpen(open);
                if (!open) resetForm();
              }}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="add-product-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Novo Produto
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-card border-white/10">
                  <DialogHeader>
                    <DialogTitle className="font-serif text-2xl">
                      {editingProduct ? 'Editar Produto' : 'Novo Produto'}
                    </DialogTitle>
                  </DialogHeader>

                  <div className="space-y-6 mt-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="form-group">
                        <Label className="form-label">Nome</Label>
                        <Input
                          value={productForm.name}
                          onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                          className="input-luxury"
                          data-testid="product-name-input"
                        />
                      </div>
                      <div className="form-group">
                        <Label className="form-label">Preço</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={productForm.price}
                          onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                          className="input-luxury"
                          data-testid="product-price-input"
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <Label className="form-label">Descrição</Label>
                      <Textarea
                        value={productForm.description}
                        onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                        className="input-luxury min-h-[100px] border border-white/20"
                        data-testid="product-description-input"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="form-group">
                        <Label className="form-label">Categoria</Label>
                        <Select
                          value={productForm.category}
                          onValueChange={(value) => setProductForm({ ...productForm, category: value })}
                        >
                          <SelectTrigger className="bg-transparent border-b border-white/20 rounded-none" data-testid="product-category-select">
                            <SelectValue placeholder="Selecione" />
                          </SelectTrigger>
                          <SelectContent className="bg-card border-white/10">
                            {categories.map((cat) => (
                              <SelectItem key={cat.id} value={cat.id}>
                                {cat.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="form-group">
                        <Label className="form-label">Estoque</Label>
                        <Input
                          type="number"
                          value={productForm.stock}
                          onChange={(e) => setProductForm({ ...productForm, stock: e.target.value })}
                          className="input-luxury"
                          data-testid="product-stock-input"
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <Label className="form-label">Tamanhos</Label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {sizes.map((size) => (
                          <button
                            key={size}
                            type="button"
                            onClick={() => toggleSize(size)}
                            className={`size-btn ${productForm.sizes.includes(size) ? 'selected' : ''}`}
                          >
                            {size}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="form-group">
                      <Label className="form-label">Cores (separadas por vírgula)</Label>
                      <Input
                        value={productForm.colors.join(', ')}
                        onChange={(e) => setProductForm({ 
                          ...productForm, 
                          colors: e.target.value.split(',').map(c => c.trim())
                        })}
                        placeholder="Preto, Vermelho, Nude"
                        className="input-luxury"
                        data-testid="product-colors-input"
                      />
                    </div>

                    <div className="form-group">
                      <Label className="form-label">Imagens</Label>
                      <div className="space-y-3">
                        {productForm.images.map((img, idx) => (
                          <div key={idx} className="space-y-2">
                            <div className="flex gap-2">
                              <Input
                                value={img}
                                onChange={(e) => updateImage(idx, e.target.value)}
                                placeholder="Cole a URL ou faça upload abaixo..."
                                className="input-luxury flex-1"
                              />
                              {productForm.images.length > 1 && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  onClick={() => removeImage(idx)}
                                  className="text-red-500"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleImageUpload(e, idx)}
                                className="hidden"
                                id={`image-upload-${idx}`}
                              />
                              <label
                                htmlFor={`image-upload-${idx}`}
                                className="flex items-center gap-2 px-3 py-1.5 text-sm border border-white/20 rounded cursor-pointer hover:bg-white/5 transition-colors"
                              >
                                {uploadingImage ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Upload className="w-4 h-4" />
                                )}
                                {uploadingImage ? 'Enviando...' : 'Upload'}
                              </label>
                              {img && (
                                <img 
                                  src={img} 
                                  alt="Preview" 
                                  className="w-10 h-10 object-cover rounded border border-white/10"
                                />
                              )}
                            </div>
                          </div>
                        ))}
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={addImageField}
                          className="btn-ghost"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Adicionar Imagem
                        </Button>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <Switch
                        checked={productForm.featured}
                        onCheckedChange={(checked) => setProductForm({ ...productForm, featured: checked })}
                        data-testid="product-featured-switch"
                      />
                      <Label>Produto em Destaque</Label>
                    </div>

                    <div className="flex justify-end gap-4 pt-4">
                      <Button
                        variant="ghost"
                        onClick={() => setProductDialogOpen(false)}
                        className="btn-ghost"
                      >
                        Cancelar
                      </Button>
                      <Button
                        onClick={handleSaveProduct}
                        className="btn-primary"
                        data-testid="save-product-btn"
                      >
                        <Save className="w-4 h-4 mr-2" />
                        Salvar
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {/* Products Table */}
            <div className="bg-card border border-white/10 overflow-hidden">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Produto</th>
                    <th>Categoria</th>
                    <th>Preço</th>
                    <th>Estoque</th>
                    <th>Destaque</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product) => (
                    <tr key={product.id} data-testid={`product-row-${product.id}`}>
                      <td>
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-16 bg-background overflow-hidden">
                            <img
                              src={product.images[0]}
                              alt={product.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <span className="font-medium">{product.name}</span>
                        </div>
                      </td>
                      <td className="capitalize">{product.category}</td>
                      <td className="text-primary font-bold">${product.price.toFixed(2)}</td>
                      <td>{product.stock}</td>
                      <td>
                        <button
                          onClick={() => handleToggleFeatured(product)}
                          className={`p-2 rounded transition-all ${product.featured ? 'text-primary hover:text-primary/80' : 'text-muted-foreground hover:text-primary'}`}
                          data-testid={`toggle-featured-${product.id}`}
                          title={product.featured ? 'Remover dos destaques' : 'Adicionar aos destaques'}
                        >
                          {product.featured ? '★' : '☆'}
                        </button>
                      </td>
                      <td>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditDialog(product)}
                            className="hover:text-primary"
                            data-testid={`edit-product-${product.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteProduct(product.id)}
                            className="hover:text-red-500"
                            data-testid={`delete-product-${product.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders" className="space-y-4">
            {orders.length === 0 ? (
              <div className="empty-state">
                <Package className="w-16 h-16 text-muted-foreground mb-6" />
                <h2 className="font-serif text-2xl">Nenhum pedido ainda</h2>
              </div>
            ) : (
              orders.map((order) => (
                <div 
                  key={order.id} 
                  className="bg-card border border-white/10 overflow-hidden"
                  data-testid={`admin-order-${order.id}`}
                >
                  <button
                    onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
                    className="w-full p-6 flex flex-col md:flex-row md:items-center justify-between gap-4 hover:bg-white/5 transition-colors"
                  >
                    <div className="flex flex-col md:flex-row md:items-center gap-4">
                      <div className="text-left">
                        <p className="text-sm text-muted-foreground">Pedido</p>
                        <p className="text-lg font-bold text-primary">#{order.id}</p>
                      </div>
                      <div className="text-left">
                        <p className="text-sm text-muted-foreground">Cliente</p>
                        <p>{order.user_name}</p>
                      </div>
                      <div className="text-left">
                        <p className="text-sm text-muted-foreground">Data</p>
                        <p>{formatDate(order.created_at)}</p>
                      </div>
                      <div className="text-left">
                        <p className="text-sm text-muted-foreground">Total</p>
                        <p className="font-bold">${order.total.toFixed(2)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex gap-2">
                        <span className={`status-badge ${getStatusColor(order.order_status)}`}>
                          {order.order_status}
                        </span>
                        <span className={`status-badge ${getStatusColor(order.payment_status)}`}>
                          {order.payment_status}
                        </span>
                      </div>
                      {expandedOrder === order.id ? (
                        <ChevronUp className="w-5 h-5" />
                      ) : (
                        <ChevronDown className="w-5 h-5" />
                      )}
                    </div>
                  </button>

                  {expandedOrder === order.id && (
                    <div className="border-t border-white/10 p-6 space-y-6 animate-fade-in">
                      {/* Update Status */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label className="form-label">Status do Pedido</Label>
                          <Select
                            value={order.order_status}
                            onValueChange={(value) => handleUpdateOrderStatus(order.id, 'order_status', value)}
                          >
                            <SelectTrigger className="bg-transparent border border-white/20 rounded-none mt-2">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-card border-white/10">
                              <SelectItem value="processing">Processando</SelectItem>
                              <SelectItem value="confirmed">Confirmado</SelectItem>
                              <SelectItem value="preparing">Preparando</SelectItem>
                              <SelectItem value="shipped">Enviado</SelectItem>
                              <SelectItem value="in_transit">Em Trânsito</SelectItem>
                              <SelectItem value="out_for_delivery">Saiu para Entrega</SelectItem>
                              <SelectItem value="delivered">Entregue</SelectItem>
                              <SelectItem value="cancelled">Cancelado</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="form-label">Status do Pagamento</Label>
                          <Select
                            value={order.payment_status}
                            onValueChange={(value) => handleUpdateOrderStatus(order.id, 'payment_status', value)}
                          >
                            <SelectTrigger className="bg-transparent border border-white/20 rounded-none mt-2">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-card border-white/10">
                              <SelectItem value="pending">Pendente</SelectItem>
                              <SelectItem value="paid">Pago</SelectItem>
                              <SelectItem value="refunded">Reembolsado</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      {/* Tracking Info */}
                      <div className="bg-white/5 p-4 rounded">
                        <h4 className="text-sm uppercase tracking-wider font-bold mb-4">📦 Rastreamento</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label className="form-label">Código de Rastreio</Label>
                            <Input
                              defaultValue={order.tracking_code || ''}
                              placeholder="Ex: BR123456789BR"
                              className="input-luxury mt-2"
                              onBlur={(e) => {
                                if (e.target.value !== (order.tracking_code || '')) {
                                  handleUpdateTracking(order.id, e.target.value, order.tracking_url || '');
                                }
                              }}
                            />
                          </div>
                          <div>
                            <Label className="form-label">Link de Rastreio</Label>
                            <Input
                              defaultValue={order.tracking_url || ''}
                              placeholder="https://..."
                              className="input-luxury mt-2"
                              onBlur={(e) => {
                                if (e.target.value !== (order.tracking_url || '')) {
                                  handleUpdateTracking(order.id, order.tracking_code || '', e.target.value);
                                }
                              }}
                            />
                          </div>
                        </div>
                        {order.tracking_code && (
                          <p className="text-sm text-muted-foreground mt-3">
                            Link público: <a href={`/rastrear?id=${order.id}`} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">/rastrear?id={order.id}</a>
                          </p>
                        )}
                      </div>

                      {/* Items */}
                      <div>
                        <h4 className="text-sm uppercase tracking-wider font-bold mb-4">Itens</h4>
                        <div className="space-y-3">
                          {order.items.map((item, idx) => (
                            <div key={idx} className="flex justify-between items-center">
                              <div>
                                <p>{item.product_name}</p>
                                <p className="text-sm text-muted-foreground">
                                  {item.size} | {item.color} | x{item.quantity}
                                </p>
                              </div>
                              <p className="text-primary">${(item.price * item.quantity).toFixed(2)}</p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Customer Info */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="text-sm uppercase tracking-wider font-bold mb-4">Cliente</h4>
                          <p>{order.user_name}</p>
                          <p className="text-muted-foreground">{order.user_email}</p>
                        </div>
                        <div>
                          <h4 className="text-sm uppercase tracking-wider font-bold mb-4">Endereço</h4>
                          <div className="text-muted-foreground">
                            <p>{order.shipping_address.street}</p>
                            <p>
                              {order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zip_code}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Payment */}
                      <div>
                        <h4 className="text-sm uppercase tracking-wider font-bold mb-4">Pagamento</h4>
                        <p className="capitalize">
                          {order.payment_method === 'cash' ? 'Dinheiro na Entrega' : 'Zelle'}
                        </p>
                      </div>

                      {order.notes && (
                        <div>
                          <h4 className="text-sm uppercase tracking-wider font-bold mb-4">Observações</h4>
                          <p className="text-muted-foreground">{order.notes}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-6">
            {/* Featured Products Section */}
            <div className="bg-card border border-white/10 p-8">
              <h2 className="font-serif text-2xl mb-6">⭐ Produtos em Destaque</h2>
              <p className="text-muted-foreground text-sm mb-6">
                Selecione quais produtos aparecem na página inicial como destaque
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
                {products.map((product) => (
                  <div 
                    key={product.id}
                    className={`p-4 border cursor-pointer transition-all ${
                      product.featured 
                        ? 'border-primary bg-primary/10' 
                        : 'border-white/10 hover:border-white/30'
                    }`}
                    onClick={async () => {
                      try {
                        await axios.put(`${API}/admin/products/${product.id}`, {
                          featured: !product.featured
                        });
                        fetchData();
                        toast.success(product.featured ? 'Removido dos destaques' : 'Adicionado aos destaques');
                      } catch (error) {
                        toast.error('Erro ao atualizar');
                      }
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-16 h-20 bg-background overflow-hidden flex-shrink-0">
                        <img
                          src={product.images[0]}
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{product.name}</p>
                        <p className="text-xs text-primary">${product.price.toFixed(2)}</p>
                        {product.featured && (
                          <span className="text-xs text-primary">⭐ Destaque</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-card border border-white/10 p-8 max-w-2xl">
              <h2 className="font-serif text-2xl mb-8">Configurações da Loja</h2>
              
              {/* Zelle Settings */}
              <div className="mb-8">
                <h3 className="text-lg font-bold mb-4 text-primary">💳 Dados do Zelle</h3>
                <div className="space-y-4">
                  <div className="form-group">
                    <Label className="form-label">Email do Zelle</Label>
                    <Input
                      value={settings.zelle_email || ''}
                      onChange={(e) => setSettings({...settings, zelle_email: e.target.value})}
                      placeholder="seu@email.com"
                      className="input-luxury"
                      data-testid="settings-zelle-email"
                    />
                  </div>
                  <div className="form-group">
                    <Label className="form-label">Telefone do Zelle</Label>
                    <Input
                      value={settings.zelle_phone || ''}
                      onChange={(e) => setSettings({...settings, zelle_phone: e.target.value})}
                      placeholder="+1 (xxx) xxx-xxxx"
                      className="input-luxury"
                      data-testid="settings-zelle-phone"
                    />
                  </div>
                  <div className="form-group">
                    <Label className="form-label">Nome no Zelle</Label>
                    <Input
                      value={settings.zelle_name || ''}
                      onChange={(e) => setSettings({...settings, zelle_name: e.target.value})}
                      placeholder="Nome da conta Zelle"
                      className="input-luxury"
                      data-testid="settings-zelle-name"
                    />
                  </div>
                </div>
              </div>

              {/* Contact Settings */}
              <div className="mb-8">
                <h3 className="text-lg font-bold mb-4 text-primary">📞 Dados de Contato</h3>
                <div className="space-y-4">
                  <div className="form-group">
                    <Label className="form-label">Email de Contato</Label>
                    <Input
                      value={settings.contact_email || ''}
                      onChange={(e) => setSettings({...settings, contact_email: e.target.value})}
                      placeholder="contato@email.com"
                      className="input-luxury"
                      data-testid="settings-contact-email"
                    />
                  </div>
                  <div className="form-group">
                    <Label className="form-label">Telefone de Contato</Label>
                    <Input
                      value={settings.contact_phone || ''}
                      onChange={(e) => setSettings({...settings, contact_phone: e.target.value})}
                      placeholder="+1 (xxx) xxx-xxxx"
                      className="input-luxury"
                      data-testid="settings-contact-phone"
                    />
                  </div>
                  <div className="form-group">
                    <Label className="form-label">Instagram (link completo)</Label>
                    <Input
                      value={settings.instagram || ''}
                      onChange={(e) => setSettings({...settings, instagram: e.target.value})}
                      placeholder="https://instagram.com/sua_conta"
                      className="input-luxury"
                      data-testid="settings-instagram"
                    />
                  </div>
                </div>
              </div>

              <Button
                onClick={handleSaveSettings}
                disabled={savingSettings}
                className="btn-primary"
                data-testid="save-settings-btn"
              >
                {savingSettings ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                    Salvando...
                  </span>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Salvar Configurações
                  </>
                )}
              </Button>
            </div>

            {/* Login Info */}
            <div className="bg-card border border-white/10 p-8 max-w-2xl">
              <h3 className="text-lg font-bold mb-4 text-primary">🔐 Dados de Acesso Admin</h3>
              <div className="space-y-2 text-sm">
                <p><strong>Email:</strong> admin@camielegance.com</p>
                <p><strong>Senha:</strong> C25a10s01</p>
              </div>
              <p className="text-xs text-muted-foreground mt-4">
                Guarde essas informações em local seguro!
              </p>
            </div>

            {/* Cleanup Section */}
            <div className="bg-card border border-red-500/30 p-8 max-w-2xl">
              <h3 className="text-lg font-bold mb-4 text-red-500">🗑️ Limpeza de Dados</h3>
              <p className="text-muted-foreground text-sm mb-4">
                Remove produtos de exemplo (com fotos do Unsplash/Pexels) que podem ter sido criados automaticamente.
              </p>
              <Button
                variant="outline"
                className="border-red-500/50 text-red-500 hover:bg-red-500/10"
                onClick={async () => {
                  if (!confirm('Tem certeza? Isso removerá os produtos de exemplo antigos.')) return;
                  try {
                    const res = await axios.post(`${API}/admin/cleanup-old-products`);
                    toast.success(res.data.message);
                    fetchData();
                  } catch (error) {
                    toast.error('Erro ao limpar produtos');
                  }
                }}
                data-testid="cleanup-products-btn"
              >
                Limpar Produtos Antigos
              </Button>
            </div>
          </TabsContent>

          {/* Coupons Tab */}
          <TabsContent value="coupons" className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="font-serif text-2xl">Cupons de Desconto</h2>
              <Dialog open={couponDialogOpen} onOpenChange={setCouponDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="add-coupon-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Novo Cupom
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md bg-card border-white/10">
                  <DialogHeader>
                    <DialogTitle className="font-serif text-2xl">Criar Cupom</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateCoupon} className="space-y-4 mt-4">
                    <div className="form-group">
                      <Label className="form-label">Código do Cupom</Label>
                      <Input
                        value={couponForm.code}
                        onChange={(e) => setCouponForm({...couponForm, code: e.target.value.toUpperCase()})}
                        placeholder="Ex: DESCONTO10"
                        className="input-luxury uppercase"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="form-group">
                        <Label className="form-label">Tipo</Label>
                        <Select
                          value={couponForm.discount_type}
                          onValueChange={(value) => setCouponForm({...couponForm, discount_type: value})}
                        >
                          <SelectTrigger className="bg-transparent border border-white/20 rounded-none">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-card border-white/10">
                            <SelectItem value="percentage">
                              <span className="flex items-center gap-2">
                                <Percent className="w-4 h-4" /> Porcentagem
                              </span>
                            </SelectItem>
                            <SelectItem value="fixed">
                              <span className="flex items-center gap-2">
                                <DollarSign className="w-4 h-4" /> Valor Fixo
                              </span>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="form-group">
                        <Label className="form-label">
                          {couponForm.discount_type === 'percentage' ? 'Desconto (%)' : 'Desconto ($)'}
                        </Label>
                        <Input
                          type="number"
                          value={couponForm.discount_value}
                          onChange={(e) => setCouponForm({...couponForm, discount_value: e.target.value})}
                          placeholder={couponForm.discount_type === 'percentage' ? '10' : '25.00'}
                          className="input-luxury"
                          required
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="form-group">
                        <Label className="form-label">Pedido Mínimo ($)</Label>
                        <Input
                          type="number"
                          value={couponForm.min_order_value}
                          onChange={(e) => setCouponForm({...couponForm, min_order_value: e.target.value})}
                          placeholder="0"
                          className="input-luxury"
                        />
                      </div>
                      <div className="form-group">
                        <Label className="form-label">Máximo de Usos</Label>
                        <Input
                          type="number"
                          value={couponForm.max_uses}
                          onChange={(e) => setCouponForm({...couponForm, max_uses: e.target.value})}
                          placeholder="Ilimitado"
                          className="input-luxury"
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <Label className="form-label">Expira em</Label>
                      <Input
                        type="date"
                        value={couponForm.expires_at}
                        onChange={(e) => setCouponForm({...couponForm, expires_at: e.target.value})}
                        className="input-luxury"
                      />
                    </div>
                    <div className="flex items-center gap-3">
                      <Switch
                        checked={couponForm.is_active}
                        onCheckedChange={(checked) => setCouponForm({...couponForm, is_active: checked})}
                      />
                      <Label>Cupom Ativo</Label>
                    </div>
                    <Button type="submit" className="btn-primary w-full">
                      <Save className="w-4 h-4 mr-2" />
                      Criar Cupom
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Coupons List */}
            <div className="bg-card border border-white/10 overflow-hidden">
              {coupons.length === 0 ? (
                <div className="p-12 text-center text-muted-foreground">
                  <Tag className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Nenhum cupom criado ainda</p>
                  <p className="text-sm mt-2">Crie seu primeiro cupom para oferecer descontos!</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-white/5">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs uppercase tracking-wider">Código</th>
                      <th className="px-6 py-4 text-left text-xs uppercase tracking-wider">Desconto</th>
                      <th className="px-6 py-4 text-left text-xs uppercase tracking-wider">Mín. Pedido</th>
                      <th className="px-6 py-4 text-left text-xs uppercase tracking-wider">Usos</th>
                      <th className="px-6 py-4 text-left text-xs uppercase tracking-wider">Status</th>
                      <th className="px-6 py-4 text-right text-xs uppercase tracking-wider">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/10">
                    {coupons.map((coupon) => (
                      <tr key={coupon.id} className="hover:bg-white/5" data-testid={`coupon-${coupon.code}`}>
                        <td className="px-6 py-4">
                          <code className="bg-primary/20 text-primary px-2 py-1 rounded font-mono">
                            {coupon.code}
                          </code>
                        </td>
                        <td className="px-6 py-4">
                          {coupon.discount_type === 'percentage' 
                            ? `${coupon.discount_value}%` 
                            : `$${coupon.discount_value.toFixed(2)}`
                          }
                        </td>
                        <td className="px-6 py-4 text-muted-foreground">
                          {coupon.min_order_value > 0 ? `$${coupon.min_order_value.toFixed(2)}` : '-'}
                        </td>
                        <td className="px-6 py-4 text-muted-foreground">
                          {coupon.times_used}{coupon.max_uses ? `/${coupon.max_uses}` : ''}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded text-xs ${
                            coupon.is_active 
                              ? 'bg-green-500/20 text-green-500' 
                              : 'bg-red-500/20 text-red-500'
                          }`}>
                            {coupon.is_active ? 'Ativo' : 'Inativo'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteCoupon(coupon.id)}
                            className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Coupon Usage Instructions */}
            <div className="bg-card border border-white/10 p-6 max-w-2xl">
              <h3 className="font-bold mb-3">💡 Como funciona</h3>
              <ul className="text-sm text-muted-foreground space-y-2">
                <li>• Clientes aplicam o código no checkout</li>
                <li>• O desconto é calculado automaticamente</li>
                <li>• Cupons expirados ou esgotados são rejeitados</li>
              </ul>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
